import { Zzz } from 'assets/icons/revamp';
import Tooltip from 'components/common/Tooltip';
import BaseButton from 'components/ui/BaseButton';
import TimeWheelPicker from 'components/ui/time-wheel-picker';
import {
  CUTOFF_TIME_AFTER_SHUTDOWN_OFFSET_MINUTES,
  TIMEOUT_DELAY
} from 'constants/general';
import COLOR from 'constants/color';
import { useEffect, useRef } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { timingSelector } from 'store/reducer/setting/selectors';
import {
  setHighlightCutoffTime,
  updateCutoffTimeForNonHighPriority
} from 'store/reducer/setting/slice';
import { addMinutesToTime, formatTimeTo12Hour } from 'utils/time.util';
import { createHtml, runAfterDelay } from 'utils/helpers';
import { TIME_WHEEL_PICKER_REMOUNT_KEY } from 'constants/time-wheel-picker';
import { ATTRIB } from 'constants/test';

const GlobalCutoffTimeRevamp: React.FC = () => {
  const dispatch = useAppDispatch();
  const cutoffWrapperRef = useRef<HTMLDivElement>(null);
  const {
    globalCutoffTime,
    shutdown_time,
    isSettingLoading,
    highlightCutoffTime,
    routineTimeValidation: { isValid, errorMessages }
  } = useAppSelectorShallow(timingSelector);
  const cutoffTimeErrors = errorMessages.cutoffTime ?? [];

  useEffect(() => {
    if (highlightCutoffTime && cutoffWrapperRef.current) {
      cutoffWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      const timer = runAfterDelay(() => {
        dispatch(setHighlightCutoffTime(false));
      }, TIMEOUT_DELAY.HIGHLIGHT_SHUTDOWN_TIME);

      return () => clearTimeout(timer);
    }
  }, [highlightCutoffTime, dispatch]);

  const handleSetCutoffTime = () => {
    dispatch(
      updateCutoffTimeForNonHighPriority(
        addMinutesToTime(
          shutdown_time ?? '',
          CUTOFF_TIME_AFTER_SHUTDOWN_OFFSET_MINUTES
        )
      )
    );
  };

  return (
    <div
      ref={cutoffWrapperRef}
      className={`grid grid-cols-1 sm:grid-cols-8 py-2 ~gap-x-4/8 ~gap-y-2/4 align-middle transition-all duration-500 ${
        highlightCutoffTime
          ? '~mx-2/4 ~p-2/4 ring-2 ring-primary-500 dark:ring-primary-dark-600 ring-offset-2 ring-offset-secondary-50 dark:ring-offset-secondary-dark-100 rounded-lg bg-primary-50 dark:bg-primary-dark-50'
          : ''
      }`}
      data-testid={ATTRIB.DATA_TESTID.CUTOFF_TIME_WRAPPER}>
      <h5 className='col-span-6 md:col-span-5 font-medium text-gray-900'>
        <Zzz
          className='~w-3/6 ~h-3/6 inline-block ~mr-1.5/3 ~mb-0.5/1.5 bg-primary-600 dark:bg-primary-800 rounded-full'
          fill={COLOR.WHITE}
          fillDarkMode={COLOR.WHITE}
        />
        {t('settings_page.choose_your_bedtime')}
        <Tooltip
          element={
            <span>
              {createHtml(
                t('settings_page.explanation_global_cutoff_time', {
                  sleep_time: globalCutoffTime
                    ? formatTimeTo12Hour(globalCutoffTime)
                    : t('settings_page.start_winding_down_time')
                })
              )}
            </span>
          }
        />
      </h5>

      {!globalCutoffTime ? (
        <div className='col-span-3 flex items-center ~gap-2/3 flex-wrap'>
          <span className='~text-xs/sm text-gray-600 dark:text-gray-400'>
            {t('settings_page.no_tech_curfew_time_set')}
          </span>
          <BaseButton
            title={t('settings_page.set_time')}
            onClick={handleSetCutoffTime}
            size='xs'
          />
        </div>
      ) : (
        <div className='col-span-3 flex items-center ~gap-2/3 flex-wrap'>
          <TimeWheelPicker
            key={`cutoff-${
              isSettingLoading
                ? TIME_WHEEL_PICKER_REMOUNT_KEY.LOADING
                : TIME_WHEEL_PICKER_REMOUNT_KEY.READY
            }`}
            initialValue={globalCutoffTime}
            onChange={(formatted24HourTime) => {
              dispatch(updateCutoffTimeForNonHighPriority(formatted24HourTime));
            }}
            size='small'
            isLoading={isSettingLoading}
          />
          <BaseButton
            title={t('settings_page.clear')}
            variant='secondary'
            onClick={() =>
              dispatch(updateCutoffTimeForNonHighPriority(undefined))
            }
            size='xs'
          />
        </div>
      )}

      {!isSettingLoading && cutoffTimeErrors.length > 0 && !isValid.cutoffTime ? (
        <ul className='col-span-8 relative mt-auto'>
          {cutoffTimeErrors.map((error) => (
            <li key={error} className='w-fit text-xs text-red-600 py-1'>
              {error}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default GlobalCutoffTimeRevamp;
